import API from "@/lib/api";
import { toast } from "sonner";

/**
 * Shared milestone / share helpers for the streak + progress toasts.
 *
 * `celebrateOnce(key, title, message, shareText)` fires a sonner toast
 * with a "Share" action once per device (deduped via localStorage under
 * `glpflo:milestone-seen:{key}`). The Share action either opens the
 * native Web Share sheet or falls back to copying a pre-formatted
 * caption to the clipboard — perfect for pasting into an Instagram
 * story caption. We do NOT auto-open Instagram (no reliable web deep
 * link that survives Chrome / Safari on both desktop and mobile), so
 * the copy fallback + native share sheet is what patients get.
 */

const SEEN_KEY = (k) => `glpflo:milestone-seen:${k}`;

/**
 * fireConfettiBurst — quick full-viewport spark shower that fires just
 * before the milestone toast so the "you did it!" moment feels earned.
 * Zero-dependency: emits ~60 absolute-positioned dots via a single
 * transient DOM overlay + inline CSS keyframes, and cleans itself up
 * after 1.8s. Reduced-motion users get a no-op so we don't flash them.
 */
function fireConfettiBurst() {
  try {
    if (typeof document === "undefined") return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const host = document.createElement("div");
    host.setAttribute("data-testid", "milestone-confetti");
    host.style.cssText =
      "position:fixed;inset:0;pointer-events:none;z-index:2147483647;overflow:hidden;";
    const colors = ["#a855f7", "#c084fc", "#10b981", "#f59e0b", "#f472b6", "#38bdf8", "#facc15"];
    const count = 60;
    const kfName = `glpflo-confetti-${Date.now().toString(36)}`;
    const style = document.createElement("style");
    style.textContent = `@keyframes ${kfName} {
      0%   { opacity: 0; transform: translate(-50%, -50%) rotate(0deg); }
      12%  { opacity: 1; }
      100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)); }
    }`;
    host.appendChild(style);
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 220 + Math.random() * 220;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist + 60; // slight downward gravity
      const size = 7 + Math.random() * 6;
      const rot = `${Math.round((Math.random() - 0.5) * 720)}deg`;
      const dur = 1.1 + Math.random() * 0.6;
      const color = colors[i % colors.length];
      const shape = Math.random() > 0.5 ? "50%" : "2px";
      const spark = document.createElement("span");
      spark.style.cssText =
        `position:absolute;left:50%;top:38%;width:${size}px;height:${size * (shape === "50%" ? 1 : 1.6)}px;` +
        `background:${color};border-radius:${shape};` +
        `box-shadow:0 0 ${size}px ${color};opacity:0;` +
        `animation:${kfName} ${dur}s cubic-bezier(.18,.79,.4,1) forwards;` +
        `--dx:${dx}px;--dy:${dy}px;--rot:${rot};`;
      host.appendChild(spark);
    }
    document.body.appendChild(host);
    setTimeout(() => { try { host.remove(); } catch { /* noop */ } }, 1800);
  } catch { /* never break the dashboard on a celebration */ }
}

export function hasSeenMilestone(key) {
  try { return !!localStorage.getItem(SEEN_KEY(key)); }
  catch { return false; }
}

export function markMilestoneSeen(key) {
  try { localStorage.setItem(SEEN_KEY(key), String(Date.now())); }
  catch { /* private-mode / quota — ignore */ }
}

async function shareOrCopy(shareText) {
  const payload = { title: "GLPFlo milestone", text: shareText, url: "https://glpflo.org" };
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share(payload);
      return "shared";
    } catch (_e) {
      // User cancelled the native share sheet — don't fall through to
      // clipboard (that would show a misleading "Copied!" toast for a
      // deliberate cancel).
      return "cancelled";
    }
  }
  try {
    await navigator.clipboard.writeText(shareText);
    return "copied";
  } catch {
    return "failed";
  }
}

export function celebrateOnce({ key, title, message, shareText }) {
  if (hasSeenMilestone(key)) return;
  markMilestoneSeen(key);
  fireConfettiBurst();
  toast.success(`${title}\n${message}`, {
    duration: 10000,
    action: {
      label: "Share",
      onClick: async () => {
        const result = await shareOrCopy(shareText);
        if (result === "copied") toast.success("Copied — paste into your Instagram story caption!");
        else if (result === "failed") toast.error("Couldn't share right now — try screenshotting the toast.");
      },
    },
  });
}

/**
 * Fetch all milestone sources in parallel and fire the celebration
 * toasts for any newly-crossed thresholds. Safe to call on every
 * dashboard mount — de-dupe happens per-key via localStorage.
 */
export async function checkAndCelebrateMilestones() {
  try {
    const [streakRes, progressRes] = await Promise.all([
      API.get("/wellness/no-symptom-streak").catch(() => null),
      API.get("/wellness/progress-milestones").catch(() => null),
    ]);
    const streak = streakRes?.data;
    const progress = progressRes?.data;

    if (streak && typeof streak.days === "number") {
      if (streak.days >= 7) {
        celebrateOnce({
          key: "streak-7",
          title: "🎉 One full week symptom-free!",
          message: "Great work sticking with it.",
          shareText: `🎉 7 days symptom-free on my GLP-1 journey with @glpflo — small wins add up! #GLPFlo #WeightLossJourney`,
        });
      }
      if (streak.days >= 30) {
        celebrateOnce({
          key: "streak-30",
          title: "🎊 30 days symptom-free!",
          message: "That's a real milestone.",
          shareText: `🎊 30 days symptom-free on my GLP-1 journey with @glpflo — feeling amazing! #GLPFlo #Milestone`,
        });
      }
    }

    if (progress?.weight_halfway?.hit) {
      celebrateOnce({
        key: "weight-halfway",
        title: "🏆 Halfway to your goal weight!",
        message: `${progress.weight_halfway.percent}% of the way there — keep going.`,
        shareText: `🏆 Halfway to my goal weight on GLPFlo — down and still going! #GLPFlo #HalfwayThere`,
      });
    }
    if (progress?.first_month_subscribed?.hit) {
      celebrateOnce({
        key: "sub-30-days",
        title: "🥳 One month of GLPFlo!",
        message: "Thanks for sticking with your care team.",
        shareText: `🥳 One month on GLPFlo — my metabolic health has never been more looked-after. #GLPFlo`,
      });
    }
  } catch { /* dashboard should never crash on a milestone check */ }
}
